import { toast } from 'react-toastify';
import { CreateTierBody, Tier, UpdateTierBody } from '@core/models/tier.model';
import { IErrorModel, ISuccessResponse } from '@core/models/generic.model';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import TierService from 'services/api/tier.service';

// -------------------------------------------------------------------------

interface ITierState {
    loading: boolean;
    error: IErrorModel | undefined;
    tiers: Tier[];
    tier: Tier | null;
}

const initialState: ITierState = {
    loading: false,
    error: {} as IErrorModel,
    tiers: [],
    tier: null,
};

// -------------------------------------------------------------------------

export const getTiersThunk = createAsyncThunk<Tier[], void, { rejectValue: IErrorModel }>(
    `/tier/getTiers`,
    async (_, { rejectWithValue }: any) => {
        try {
            const response: any = await TierService.getTiers();
            return response.data.tiers
        } catch (err: any) {
            toast.error(err.errorMessage || 'Failed to fetch tiers', { autoClose: 5000 });
            return rejectWithValue(err);
        }
    }
);

export const getTierByIdThunk = createAsyncThunk<Tier, number, { rejectValue: IErrorModel }>(
    `/tier/getTierById`,
    async (id, { rejectWithValue }: any) => {
        try {
            const response: any = await TierService.getTierById(id);
            return response.data;
        } catch (err: any) {
            toast.error(err.errorMessage || 'Failed to fetch tier', { autoClose: 5000 });
            return rejectWithValue(err);
        }
    }
);

export const createTierThunk = createAsyncThunk<ISuccessResponse, CreateTierBody, { rejectValue: IErrorModel }>(
    `/tier/createTier`,
    async (body, { rejectWithValue }: any) => {
        try {
            const response: any = await TierService.createTier(body);
            toast.success(response.data.message || 'Tier created successfully', { autoClose: 5000 });
            return response.data;
        } catch (err: any) {
            toast.error(err.errorMessage || 'Failed to create tier', { autoClose: 5000 });
            return rejectWithValue(err);
        }
    }
);

export const updateTierThunk = createAsyncThunk<ISuccessResponse, UpdateTierBody, { rejectValue: IErrorModel }>(
    `/tier/updateTier`,
    async (body, { rejectWithValue }: any) => {
        try {
            const response: any = await TierService.updateTier(body);
            toast.success(response.data.message || 'Tier updated successfully', { autoClose: 5000 });
            return response.data;
        } catch (err: any) {
            toast.error(err.errorMessage || 'Failed to update tier', { autoClose: 5000 });
            return rejectWithValue(err);
        }
    }
);

export const deleteTierThunk = createAsyncThunk<ISuccessResponse, number, { rejectValue: IErrorModel }>(
    `/tier/deleteTier`,
    async (id, { rejectWithValue }: any) => {
        try {
            const response: any = await TierService.deleteTier(id);
            toast.success(response.data.message || 'Tier deleted successfully', { autoClose: 5000 });
            return response.data;
        } catch (err: any) {
            toast.error(err.errorMessage || 'Failed to delete tier', { autoClose: 5000 });
            return rejectWithValue(err);
        }
    }
);

// -------------------------------------------------------------------------

const tierSlice = createSlice({
    name: `tier`,
    initialState,
    reducers: {
        clearTier(state) {
            state.tier = null;
        },
    },
    extraReducers: (builder) => {
        // Get Tiers
        builder.addCase(getTiersThunk.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(getTiersThunk.fulfilled, (state, action) => {
            state.loading = false;
            state.tiers = action.payload;
        });
        builder.addCase(getTiersThunk.rejected, (state, action) => {
            state.loading = false;
            state.tiers = [];
            state.error = action.payload;
        });

        // Get Tier By Id
        builder.addCase(getTierByIdThunk.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(getTierByIdThunk.fulfilled, (state, action) => {
            state.loading = false;
            state.tier = action.payload;
        });
        builder.addCase(getTierByIdThunk.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });

        // Create Tier
        builder.addCase(createTierThunk.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(createTierThunk.fulfilled, (state) => {
            state.loading = false;
        });
        builder.addCase(createTierThunk.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });

        // Update Tier
        builder.addCase(updateTierThunk.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(updateTierThunk.fulfilled, (state) => {
            state.loading = false;
        });
        builder.addCase(updateTierThunk.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });

        // Delete Tier
        builder.addCase(deleteTierThunk.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(deleteTierThunk.fulfilled, (state) => {
            state.loading = false;
        });
        builder.addCase(deleteTierThunk.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });
    },
});

// -------------------------------------------------------------------------

export default tierSlice;
export const { clearTier } = tierSlice.actions;
export const tierReducer = tierSlice.reducer;

